import { NumberInput, Select, Stack, Switch } from "@mantine/core";
import { useForm } from "@mantine/form";
import { InsuranceProviderPlanType } from "../../../services/admin/insurance-provider-plan-types.service";
import { useInsuranceProvidersAdmin } from "../../../hooks/admin/useInsuranceProvidersAdmin";
import { useInsurancePlanTypesAdmin } from "../../../hooks/admin/useInsurancePlanTypes";

export interface InsuranceProviderPlanTypeFormValues {
  insuranceProviderId: string;
  insurancePlanTypeId: string;
  allowSplitBilling: boolean;
  tebraCompanyId: number | "";
  tebraPlanId: number | "";
}

interface InsuranceProviderPlanTypeFormProps {
  initialValues?: InsuranceProviderPlanType;
  onSubmit: (values: InsuranceProviderPlanTypeFormValues) => void;
}

export default function InsuranceProviderPlanTypeForm({
  initialValues,
  onSubmit,
}: InsuranceProviderPlanTypeFormProps) {
  const { data: providers = [] } = useInsuranceProvidersAdmin();
  const { data: planTypes = [] } = useInsurancePlanTypesAdmin();

  const form = useForm<InsuranceProviderPlanTypeFormValues>({
    initialValues: {
      insuranceProviderId: initialValues?.insuranceProviderId || "",
      insurancePlanTypeId: initialValues?.insurancePlanTypeId || "",
      allowSplitBilling: initialValues?.allowSplitBilling ?? false,
      tebraCompanyId: initialValues?.tebraCompanyId ?? "",
      tebraPlanId: initialValues?.tebraPlanId ?? "",
    },
    validate: {
      insuranceProviderId: (value) =>
        !value ? "Insurance provider is required" : null,
      insurancePlanTypeId: (value) =>
        !value ? "Insurance plan type is required" : null,
      tebraCompanyId: (value, values) => {
        if (value === "" && values.tebraPlanId === "") return null;
        if (value === "")
          return "Both Tebra IDs are required when configuring a payer mapping";
        return Number.isSafeInteger(value) && value > 0 && value <= 2147483647
          ? null
          : "Tebra Company ID must be a positive integer";
      },
      tebraPlanId: (value, values) => {
        if (value === "" && values.tebraCompanyId === "") return null;
        if (value === "")
          return "Both Tebra IDs are required when configuring a payer mapping";
        return Number.isSafeInteger(value) && value > 0 && value <= 2147483647
          ? null
          : "Tebra Plan ID must be a positive integer";
      },
    },
  });

  return (
    <form noValidate onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="md">
        <Select
          label="Insurance Provider"
          placeholder="Select insurance provider"
          required
          searchable
          data={providers
            .filter((p) => p.isActive)
            .map((p) => ({
              value: p.id,
              label: p.name,
            }))}
          {...form.getInputProps("insuranceProviderId")}
          disabled={!!initialValues}
        />

        <Select
          label="Insurance Plan Type"
          placeholder="Select plan type"
          required
          searchable
          data={planTypes
            .filter((pt) => pt.isActive)
            .map((pt) => ({
              value: pt.id,
              label: pt.type,
            }))}
          {...form.getInputProps("insurancePlanTypeId")}
          disabled={!!initialValues}
        />

        <Switch
          label="Allow Split Billing (TC Only)"
          description="If enabled, billing scenario is TC Only. If disabled, billing scenario is Global."
          {...form.getInputProps("allowSplitBilling", { type: "checkbox" })}
        />

        <NumberInput
          label="Tebra Company ID"
          description="Numeric CompanyID from this Tebra account; never use the LIS provider ID or name."
          placeholder="Leave empty until mapped"
          min={1}
          max={2147483647}
          allowDecimal={false}
          clampBehavior="strict"
          {...form.getInputProps("tebraCompanyId")}
        />

        <NumberInput
          label="Tebra Plan ID"
          description="Numeric PlanID for this exact carrier and plan type in Tebra. Both IDs are required together."
          placeholder="Leave empty until mapped"
          min={1}
          max={2147483647}
          allowDecimal={false}
          clampBehavior="strict"
          {...form.getInputProps("tebraPlanId")}
        />
      </Stack>
    </form>
  );
}

export { useForm } from "@mantine/form";
